<template>
  <div class="row donation-campaigns-report">
    <div class="col-xl-12 col-md-12 col-xs-12">
      <div class="card mar-to-15 tableInvoice">
        <div class="card-content">
          <div :class="(loading ? 'show-loader' : 'hide-loader')">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>

          <div class="table-top" style="display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; flex-wrap: wrap;">
            <div class="left-group" style="display: flex; flex-wrap: wrap; gap: 8px; align-items: flex-end; flex: 1 1 auto;">
              <div class="form-group donation-campaigns-report__name-filter" style="flex: 0 0 220px; margin: 0;">
                <label>{{ $t('donationCampaigns.search') }}</label>
                <el-input
                  v-model="searchName"
                  :placeholder="$t('donationCampaigns.name')"
                  size="small"
                  clearable
                  @keyup.enter.native="applyFilters"
                />
              </div>

              <div class="form-group donation-campaigns-report__location-filter" v-if="locations.length" style="margin: 0;">
                <label>{{ $t('orders.location') }}</label>
                <el-select
                  v-model="locationFilter"
                  multiple
                  collapse-tags
                  clearable
                  filterable
                  size="small"
                  :placeholder="$t('general.select')"
                  style="width: 100%;">
                  <el-option
                    v-for="loc in locations"
                    :key="loc.true_id || loc.id"
                    :label="loc.name"
                    :value="loc.true_id">
                  </el-option>
                </el-select>
              </div>

              <div class="form-group" style="flex: 0 0 100px; margin: 0;">
                <button class="btn add" @click="applyFilters" :title="$t('reports.search')" style="width: 100%; font-size: 12px; padding: 4px 8px;">
                  <i class="ti-search"></i> {{ $t('general.search') }}
                </button>
              </div>
            </div>
          </div>

          <div class="data-table" style="margin-top: 20px;">
            <el-table
              :data="tableData"
              :empty-text="$t('gestiune.noData')"
              style="width: 100%;"
              @row-click="onRowClick"
            >
              <el-table-column prop="id" :label="$t('donationCampaigns.id')" width="80" />
              <el-table-column prop="name" :label="$t('donationCampaigns.name')" min-width="180" show-overflow-tooltip />
              <el-table-column :label="$t('donationCampaigns.location')" min-width="140">
                <template slot-scope="props">
                  <div class="locations-cell" :title="getLocationNames(props.row)">
                    <span class="locations-name">{{ getVisibleLocationName(props.row) }}</span>
                    <span v-if="getHiddenLocationCount(props.row)" class="locations-more">
                      +{{ getHiddenLocationCount(props.row) }}
                    </span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column :label="$t('donationCampaigns.startEnd')" min-width="170">
                <template slot-scope="props">
                  {{ formatDate(props.row.starts_at || props.row.date_start) }} - {{ formatDate(props.row.ends_at || props.row.date_end) }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('orders.status')" width="100">
                <template slot-scope="props">
                  {{ props.row.active ? $t('general.active') : $t('general.inactive') }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('donationCampaigns.targetAmount')" width="140">
                <template slot-scope="props">
                  {{ parseFloat(props.row.target_amount || 0).toFixed(2) }} {{ currency }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('donationCampaigns.collectedAmount')" width="140">
                <template slot-scope="props">
                  {{ parseFloat(props.row.raised_total || 0).toFixed(2) }} {{ currency }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('donationCampaigns.progress')" width="100">
                <template slot-scope="props">
                  {{ formatProgress(props.row) }}
                </template>
              </el-table-column>
            </el-table>

            <div class="donation-campaigns-report__pagination">
              <el-pagination
                layout="prev, pager, next"
                :total="paginationDef.totalRecords"
                :page-size="paginationDef.perPage"
                :current-page.sync="paginationDef.page"
                @current-change="handlePageChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { Input, Select, Option } from 'element-ui'

Vue.use(Input)
Vue.use(Select)
Vue.use(Option)

export default {
  props: {
    isDonationCampaignsReport: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: true,
      searchName: '',
      locationFilter: [],
      locations: [],
      tableData: [],
      currency: this.getCurrencySymbol(this.$session.get('selectedCompanyData')['currency_iso']),
      paginationDef: {
        page: 1,
        perPage: 25,
        totalRecords: 0
      }
    }
  },
  watch: {
    isDonationCampaignsReport (active) {
      if (active) {
        this.getCampaigns()
      }
    }
  },
  created () {
    this.initializePage()
    this.$bus.$on('refreshDonationCampaignsReport', this.getCampaigns)
  },
  beforeDestroy () {
    this.$bus.$off('refreshDonationCampaignsReport', this.getCampaigns)
  },
  methods: {
    initializePage () {
      this.locations = this.$session.get('selectedCompanyData').locations_details || []
      if (!this.isOwner()) {
        const allowedLocations = JSON.parse(this.$session.get('userDataLocations'))
        if (allowedLocations && allowedLocations.length > 0) {
          this.locations = this.locations.filter(el =>
            allowedLocations.includes(el.true_id) || allowedLocations.includes(el.id)
          )
        }
      }
      this.getCampaigns()
    },
    applyFilters () {
      this.paginationDef.page = 1
      this.getCampaigns()
    },
    handlePageChange (page) {
      this.paginationDef.page = page
      this.getCampaigns()
    },
    buildParams () {
      const params = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        page: this.paginationDef.page,
        per_page: this.paginationDef.perPage
      }
      if (this.searchName && String(this.searchName).trim()) {
        params.name = String(this.searchName).trim()
      }
      if (this.locationFilter && this.locationFilter.length) {
        params.location_ids = this.locationFilter
      }
      return params
    },
    getCampaigns () {
      if (!this.isDonationCampaignsReport) {
        return
      }
      this.loading = true
      this.apiRequest('/reports/donation-campaigns', 'GET', this.buildParams(), (response) => {
        if (response.status === true) {
          const payload = response.data || {}
          if (payload.pagination) {
            this.tableData = payload.data || []
            this.paginationDef.page = payload.pagination.page || this.paginationDef.page
            this.paginationDef.perPage = payload.pagination.per_page || payload.pagination.perPage || this.paginationDef.perPage
            this.paginationDef.totalRecords = payload.pagination.totalRecords || payload.pagination.total || 0
          } else if (Array.isArray(payload.data)) {
            this.tableData = payload.data
            this.paginationDef.totalRecords = payload.total || payload.data.length
          } else if (Array.isArray(payload)) {
            this.tableData = payload
            this.paginationDef.totalRecords = payload.length
          } else {
            this.tableData = []
            this.paginationDef.totalRecords = 0
          }
        } else {
          this.tableData = []
          this.paginationDef.totalRecords = 0
        }
        this.$nextTick(() => {
          this.loading = false
        })
      })
    },
    locationIdMatches (a, b) {
      if (a === b) return true
      if (a == null || b == null || a === '' || b === '') return false
      return String(a) === String(b)
    },
    locationMatchesId (loc, locationId) {
      return this.locationIdMatches(loc.id, locationId) || this.locationIdMatches(loc.true_id, locationId)
    },
    getRowLocationNames (row) {
      if (row.locations_formatted && row.locations_formatted.length) {
        return row.locations_formatted.map(loc => loc.name)
      }
      const locationIds = row.location_ids
      if (!locationIds || !locationIds.length || !this.locations.length) {
        return []
      }
      return this.locations
        .filter(loc => locationIds.some(locationId => this.locationMatchesId(loc, locationId)))
        .map(loc => loc.name)
    },
    getVisibleLocationName (row) {
      const names = this.getRowLocationNames(row)
      if (names.length) {
        return names[0]
      }
      if (!row.location_ids || !row.location_ids.length) {
        return this.$t('donationCampaigns.allLocations')
      }
      return '-'
    },
    getHiddenLocationCount (row) {
      const names = this.getRowLocationNames(row)
      if (names.length <= 1) {
        return 0
      }
      return names.length - 1
    },
    getLocationNames (row) {
      const names = this.getRowLocationNames(row)
      if (names.length) {
        return names.join(', ')
      }
      if (!row.location_ids || !row.location_ids.length) {
        return this.$t('donationCampaigns.allLocations')
      }
      return '-'
    },
    formatProgress (row) {
      if (row.progress != null && !isNaN(parseFloat(row.progress))) {
        return parseFloat(row.progress).toFixed(1) + '%'
      }
      const target = parseFloat(row.target_amount || 0)
      const raised = parseFloat(row.raised_total || 0)
      if (target <= 0) return '0%'
      return Math.min(100, (raised / target) * 100).toFixed(1) + '%'
    },
    onRowClick (row) {
      if (!row || row.id == null) return
      this.$router.push({
        path: `/reports/donation-campaigns/${row.id}/orders`,
        query: {
          tab: 'donation-campaigns-report',
          title: row.name || String(row.id)
        }
      })
    }
  }
}
</script>

<style scoped>
.donation-campaigns-report .form-group {
  margin-bottom: 15px;
}
.donation-campaigns-report__name-filter {
  max-width: 220px;
}
.donation-campaigns-report__location-filter {
  flex: 0 0 300px;
  min-width: 280px;
  max-width: 340px;
}
.donation-campaigns-report__location-filter >>> .el-select__tags {
  max-width: calc(100% - 24px);
  flex-wrap: nowrap;
  overflow: hidden;
}
.donation-campaigns-report__location-filter >>> .el-select__tags .el-tag:first-of-type {
  max-width: 170px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.donation-campaigns-report__location-filter >>> .el-select__tags .el-tag:last-of-type:not(:only-of-type) {
  max-width: none;
  min-width: 36px;
  flex-shrink: 0;
  overflow: visible;
  text-overflow: clip;
}
.donation-campaigns-report__pagination {
  display: flex;
  justify-content: center;
  margin-top: 15px;
}
.locations-cell {
  align-items: center;
  display: flex;
  max-width: 100%;
  overflow: hidden;
}
.locations-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.locations-more {
  background: #674BD2;
  border-radius: 10px;
  color: #fff;
  flex: 0 0 auto;
  font-size: 11px;
  line-height: 1;
  margin-left: 6px;
  padding: 3px 6px;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Reports/DonationCampaignsReport.vue